import { apiClient } from './client';
import type { ApiEnvelope } from './templates';

export type EntrySeverity = 'safety_concern' | 'recommendation' | null;

/**
 * `user_library` — manually authored. Default for `POST /api/deficiency-entries`.
 * `user_curated` — saved from an accepted/edited AI finding (issue #44). Ranks
 *                  above `ai_generated` defaults in the SuggestionsStrip.
 * `ai_generated` — Haiku output cached on first component open (issue #19).
 *                  Set server-side; not accepted from API clients.
 */
export type EntrySource = 'user_library' | 'user_curated' | 'ai_generated';

export interface DeficiencyEntry {
  id: string;
  inspectorId: string;
  title: string;
  description: string | null;
  severity: EntrySeverity;
  sectionName: string;
  componentName: string | null;
  /**
   * Short trade-routing code ("plumber", "gc", "electrician",
   * "heating/cooling", …). Surfaced as a filter chip in the library
   * and a badge on each row. Null for entries that don't have one
   * (e.g. a user-created entry or a Spectora row with no recommendation).
   */
  tradeTag: string | null;
  orderIndex: number;
  useCount: number;
  source: EntrySource;
  createdAt: string;
  updatedAt: string;
}

export interface AppliedFinding {
  id: string;
  roomId: string;
  inspectionId: string;
  title: string;
  description: string | null;
  severity: EntrySeverity;
  source: string;
  isIncluded: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ApplyResult {
  entry: DeficiencyEntry;
  finding: AppliedFinding;
}

async function unwrap<T>(p: Promise<ApiEnvelope<T>>): Promise<T> {
  const res = await p;
  if (!res.success) throw new Error(res.error ?? 'Request failed');
  return res.data;
}

export interface ListFilter {
  sectionName?: string;
  componentName?: string;
  sort?: 'order' | 'popular';
}

export interface CreateEntryInput {
  title: string;
  description?: string | null;
  severity?: EntrySeverity;
  section_name: string;
  component_name?: string | null;
  trade_tag?: string | null;
  order_index?: number;
  source?: 'user_library' | 'user_curated';
}

export interface UpdateEntryPatch {
  title?: string;
  description?: string | null;
  severity?: EntrySeverity;
  section_name?: string;
  component_name?: string | null;
  trade_tag?: string | null;
  order_index?: number;
}

function buildQuery(filter: ListFilter): string {
  const params = new URLSearchParams();
  if (filter.sectionName) params.set('sectionName', filter.sectionName);
  if (filter.componentName) params.set('componentName', filter.componentName);
  if (filter.sort) params.set('sort', filter.sort);
  const q = params.toString();
  return q ? `?${q}` : '';
}

export const deficiencyEntriesApi = {
  list: (filter: ListFilter = {}) =>
    unwrap(
      apiClient.get<ApiEnvelope<DeficiencyEntry[]>>(
        `/deficiency-entries${buildQuery(filter)}`,
      ),
    ),

  create: (input: CreateEntryInput) =>
    unwrap(apiClient.post<ApiEnvelope<DeficiencyEntry>>('/deficiency-entries', input)),

  update: (id: string, patch: UpdateEntryPatch) =>
    unwrap(apiClient.patch<ApiEnvelope<DeficiencyEntry>>(`/deficiency-entries/${id}`, patch)),

  remove: (id: string) =>
    unwrap(apiClient.delete<ApiEnvelope<{ id: string }>>(`/deficiency-entries/${id}`)),

  reorder: (sectionName: string, ids: string[]) =>
    unwrap(
      apiClient.post<ApiEnvelope<DeficiencyEntry[]>>('/deficiency-entries/reorder', {
        section_name: sectionName,
        ids,
      }),
    ),

  apply: (
    id: string,
    inspectionId: string,
    roomId: string,
    componentId?: string | null,
  ) =>
    unwrap(
      apiClient.post<ApiEnvelope<ApplyResult>>(`/deficiency-entries/${id}/apply`, {
        inspection_id: inspectionId,
        room_id: roomId,
        ...(componentId ? { component_id: componentId } : {}),
      }),
    ),
};
